import React, { useRef, useState } from 'react';
import useSnapIndex from '../../hooks/useSnapIndex';
import './ProductGallery.css';

// Product photo gallery. One list of photos, laid out two ways by CSS:
//
// Phones: a swipe strip with a "3 / 14" counter - one photo per screen, the
// next peeking in. Desktop: a mosaic of the lead photo and the next four, the
// rest behind a "Show all N photos" toggle so a long gallery does not become a
// wall.
//
// It used to render a strip and a mosaic side by side and hide one, which put
// every photo in the page twice. Now each photo exists once, whatever the
// screen, and the pre-rendered HTML is the same list the browser hydrates.
//
// `leadImage` is the selected colour's image when the customer has picked one
// in the configurator; it replaces the first photo so the gallery shows the
// awning in the fabric they chose.

const MOSAIC_COUNT = 5;

export default function ProductGallery({ images = [], leadImage, name }) {
  const [showAll, setShowAll] = useState(false);
  const trackRef = useRef(null);
  const list = leadImage ? [leadImage, ...images.filter((i) => i !== leadImage)] : images;
  const { index, canScroll } = useSnapIndex(trackRef, [list.length]);
  if (!list.length) return null;

  const hidden = list.length - MOSAIC_COUNT;

  return (
    <div className={`pg${showAll ? ' pg--all' : ''}`} role="region" aria-label={`${name} photos`}>
      <ul className="pg-list" ref={trackRef}>
        {list.map((src, i) => (
          <li className={`pg-item${i === 0 ? ' pg-item--lead' : ''}`} key={src}>
            <img src={src} alt={`${name} ${i + 1}`} width={800} height={600} loading={i === 0 ? 'eager' : 'lazy'} draggable={false} />
          </li>
        ))}
      </ul>
      {/* Only a scrolling strip has a position to report; the mosaic never
          scrolls, so the counter only ever appears on a phone. */}
      {canScroll && <div className="pg-counter" aria-live="polite">{index + 1} / {list.length}</div>}
      {hidden > 0 && !showAll && (
        <button type="button" className="pg-more" onClick={() => setShowAll(true)}>
          Show all {list.length} photos
        </button>
      )}
    </div>
  );
}
